/**
 * A subscription tier's currency rows: how they follow the Currency Config, and what a
 * change of Pricing Model does to them.
 */

import {
  OfferingFormPriceType,
  transitionOfferingFormCurrencyRows,
} from "@/features/pricing";
import {UNSET_PWYW_AMOUNT} from "../../general/pricing/pwyw-currencies";
import type {SubscriptionTierFormValues} from "./types";

/**
 * Whether a tier charges its customers: Single Fee or Pay What You Want. Both price in the
 * Currency Config currencies; only a Free tier has nothing to price.
 */
export function isChargedTier(priceType: string | undefined): boolean {
  return (priceType ?? "paid") !== "free";
}

type TierCurrencyRow = SubscriptionTierFormValues["currencies"][number];

/**
 * A tier's rows rebuilt to match the Currency Config, keeping what the coach entered for
 * every currency that survives.
 *
 * A Free tier is zeroed. A Single Fee tier keeps its price, as it always has. A Pay What
 * You Want tier keeps both amounts as entered, and a currency new to it starts empty,
 * because 0 is below the Platform Minimum and the coach must set both themselves.
 */
export function toCurrencyConfigRows(
  tier: Pick<SubscriptionTierFormValues, "priceType" | "currencies"> | undefined,
  currencyCodes: string[]
): TierCurrencyRow[] {
  const rowsByCurrency = new Map(
    (tier?.currencies ?? []).map((row) => [row.callPriceCurrency.toUpperCase(), row])
  );

  return currencyCodes.map((currencyCode) => {
    const row = rowsByCurrency.get(currencyCode.toUpperCase());

    if (tier?.priceType === "free") {
      return {callPriceCurrency: currencyCode, callPrice: 0};
    }
    if (tier?.priceType === "pwyw") {
      return {
        callPriceCurrency: currencyCode,
        callPrice: row ? row.callPrice : UNSET_PWYW_AMOUNT,
        minCallPrice: row?.minCallPrice ?? UNSET_PWYW_AMOUNT,
      };
    }
    return {callPriceCurrency: currencyCode, callPrice: Number(row?.callPrice ?? 0)};
  });
}

/**
 * A tier's rows for a change of Pricing Model.
 *
 * Between Single Fee and Pay What You Want the amounts carry over and only the Minimum
 * Price changes hands, by the same rule every offering follows. Free has no amounts to
 * carry, in either direction. The currencies themselves never change: they are the
 * Currency Config's.
 */
export function transitionTierCurrencyRows(
  from: OfferingFormPriceType,
  to: OfferingFormPriceType,
  rows: TierCurrencyRow[]
): TierCurrencyRow[] {
  const currencyCodes = rows.map((row) => row.callPriceCurrency);

  if (from === "free" || to === "free") {
    return toCurrencyConfigRows({priceType: to, currencies: []}, currencyCodes);
  }

  const carried = transitionOfferingFormCurrencyRows(from, to, rows) ?? rows;

  return toCurrencyConfigRows(
    {
      priceType: to,
      currencies: carried.map((row) => ({
        callPriceCurrency: row.callPriceCurrency,
        callPrice: row.callPrice ?? UNSET_PWYW_AMOUNT,
        ...(to === "pwyw" ? {minCallPrice: row.minCallPrice ?? UNSET_PWYW_AMOUNT} : {}),
      })),
    },
    currencyCodes
  );
}
